import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getApi, postApi, patchApi, deleteApi } from '../../Services/apiService';
import {
  AdminStatCard,
  AdminSearch,
  AdminBadge,
  AdminModal,
  AdminModalFooter,
  AdminPagination,
} from '../components/AdminUi';
import Input from '../../Components/Input/Input';
import Button from '../../Components/Button/Button';

const APPLIES_TO_OPTIONS = [
  { value: 'both', label: 'Both' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'screening', label: 'Tenant Screening' },
];

const defaultPagination = {
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
};

const emptyForm = {
  code: '',
  discount_value: '',
  applies_to: 'screening',
  max_redemptions: '',
  expires_at: '',
};

function isTruthyFlag(value) {
  return value === 1 || value === '1' || value === true || value === 'true';
}

function appliesToLabel(value) {
  const match = APPLIES_TO_OPTIONS.find((option) => option.value === String(value ?? '').toLowerCase());
  if (match) return match.label;
  if (value == null || value === '') return '—';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function todayISODate() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function toDate(timestamp) {
  if (timestamp == null || timestamp === '' || Number(timestamp) === 0) return null;
  const numeric = Number(timestamp);
  // API sends epoch values; seconds and milliseconds are both tolerated.
  const ms = Number.isNaN(numeric)
    ? Date.parse(timestamp)
    : numeric < 1e12
      ? numeric * 1000
      : numeric;
  const date = new Date(ms);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatExpiry(timestamp) {
  const date = toDate(timestamp);
  if (!date) return 'Never';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function toDateInputValue(timestamp) {
  const date = toDate(timestamp);
  if (!date) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function isExpired(discount) {
  const date = toDate(discount.expires_at);
  return !!date && date.getTime() < Date.now();
}

function normalizeDiscount(discount) {
  return {
    ...discount,
    id: discount.id,
    code: discount.code ?? '',
    discount_value: discount.discount_value ?? discount.discount_percent ?? discount.percent_off ?? 0,
    applies_to: discount.applies_to ?? 'both',
    max_redemptions: Number(discount.max_redemptions ?? 0),
    times_redeemed: Number(discount.times_redeemed ?? discount.redemptions ?? 0),
    expires_at: discount.expires_at ?? null,
    is_active: isTruthyFlag(discount.is_active ?? discount.active),
  };
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.discounts)) return payload.discounts;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function normalizePagination(pag, totalFallback, fallbackPageSize) {
  const pageSize = Number(pag?.pageSize ?? pag?.limit ?? fallbackPageSize ?? 10);
  const totalItems = Number(pag?.totalItems ?? pag?.total ?? totalFallback ?? 0);
  const totalPages = Number(pag?.totalPages ?? Math.max(1, Math.ceil(totalItems / pageSize)));
  return {
    currentPage: Number(pag?.currentPage ?? pag?.page ?? 1),
    pageSize,
    totalPages,
    totalItems,
  };
}

export default function DiscountsPage() {
  const [discounts, setDiscounts] = useState([]);
  const [pagination, setPagination] = useState(defaultPagination);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [togglingId, setTogglingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const searchTimeoutRef = useRef(null);
  const previousDebouncedSearchRef = useRef('');

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;

    const fetchDiscounts = async () => {
      setIsLoading(true);
      try {
        const params = {
          page: pagination.currentPage,
          limit: pagination.pageSize,
        };

        if (debouncedSearch.trim()) {
          params.search = debouncedSearch.trim();
        }

        const response = await getApi('/admin/discounts', { params });
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          const list = extractList(response.data).map(normalizeDiscount);
          setDiscounts(list);
          setPagination((prev) =>
            normalizePagination(
              response.data.pagination ?? response.data,
              list.length,
              prev.pageSize
            )
          );
        } else {
          toast.error(response?.message || 'Failed to load discounts');
          setDiscounts([]);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load discounts';
          toast.error(message);
          setDiscounts([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchDiscounts();
    return () => {
      cancelled = true;
    };
  }, [pagination.currentPage, pagination.pageSize, debouncedSearch, listRefreshKey]);

  useEffect(() => {
    if (debouncedSearch !== previousDebouncedSearchRef.current && pagination.currentPage !== 1) {
      previousDebouncedSearchRef.current = debouncedSearch;
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      return;
    }
    previousDebouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch, pagination.currentPage]);

  const stats = useMemo(() => {
    const active = discounts.filter((d) => d.is_active && !isExpired(d)).length;
    const expired = discounts.filter((d) => isExpired(d)).length;
    const redemptions = discounts.reduce((sum, d) => sum + (d.times_redeemed || 0), 0);
    return {
      total: pagination.totalItems || discounts.length,
      active,
      expired,
      redemptions,
    };
  }, [discounts, pagination.totalItems]);

  const closeAddModal = () => {
    if (isSaving) return;
    setAddOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setAddOpen(true);
  };

  const openEditModal = (discount) => {
    setEditingId(discount.id);
    setForm({
      code: discount.code ?? '',
      discount_value: String(discount.discount_value ?? ''),
      applies_to: discount.applies_to ?? 'screening',
      max_redemptions: discount.max_redemptions > 0 ? String(discount.max_redemptions) : '',
      expires_at: toDateInputValue(discount.expires_at),
    });
    setAddOpen(true);
  };

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (isSaving) return;

    const editingDiscount = editingId ? discounts.find((d) => d.id === editingId) : null;
    if (editingId && !editingDiscount) return;

    const code = form.code.trim().toUpperCase();
    if (!code) {
      toast.error('Discount code is required');
      return;
    }

    const discountValue = Number(form.discount_value);
    if (!form.discount_value || Number.isNaN(discountValue) || discountValue <= 0 || discountValue > 100) {
      toast.error('Enter a discount percentage between 1 and 100');
      return;
    }

    const maxRedemptions = form.max_redemptions === '' ? 0 : Number(form.max_redemptions);
    if (Number.isNaN(maxRedemptions) || maxRedemptions < 0) {
      toast.error('Max redemptions must be 0 (unlimited) or a positive number');
      return;
    }

    if (editingDiscount && maxRedemptions > 0 && maxRedemptions < editingDiscount.times_redeemed) {
      toast.error(
        `Max redemptions cannot be lower than the ${editingDiscount.times_redeemed} already redeemed`
      );
      return;
    }

    let expiresAt = 0;
    if (form.expires_at) {
      const parsed = new Date(`${form.expires_at}T23:59:59`);
      if (Number.isNaN(parsed.getTime())) {
        toast.error('Enter a valid expiry date');
        return;
      }
      // An untouched expiry on an existing discount may already be in the past.
      const expiryChanged =
        !editingDiscount || form.expires_at !== toDateInputValue(editingDiscount.expires_at);
      if (expiryChanged && parsed.getTime() <= Date.now()) {
        toast.error('Expiry date must be in the future');
        return;
      }
      expiresAt = parsed.getTime();
    }

    const payload = {
      code,
      discount_value: discountValue,
      applies_to: form.applies_to,
      max_redemptions: maxRedemptions,
      expires_at: expiresAt,
    };

    setIsSaving(true);
    try {
      const response = editingId
        ? await patchApi(`/admin/discounts/${editingId}`, payload)
        : await postApi('/admin/discounts', payload);

      if (response?.statusCode === 200) {
        toast.success(
          response?.message ||
            (editingId ? 'Discount updated successfully' : 'Discount created successfully')
        );
        setAddOpen(false);
        setEditingId(null);
        setForm(emptyForm);
        if (!editingId) {
          setPagination((prev) => ({ ...prev, currentPage: 1 }));
        }
        setListRefreshKey((key) => key + 1);
      } else {
        toast.error(
          response?.message || (editingId ? 'Failed to update discount' : 'Failed to create discount')
        );
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404 ? 'Discount not found' : null) ||
        error?.message ||
        (editingId ? 'Failed to update discount' : 'Failed to create discount');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (discount) => {
    if (togglingId === discount.id) return;

    const nextActive = !discount.is_active;
    setTogglingId(discount.id);
    setDiscounts((prev) =>
      prev.map((d) => (d.id === discount.id ? { ...d, is_active: nextActive } : d))
    );

    try {
      const response = await patchApi(`/admin/discounts/${discount.id}/status`, {
        is_active: nextActive,
      });

      if (response?.statusCode === 200) {
        toast.success(
          response?.message || `Discount ${nextActive ? 'activated' : 'deactivated'}`
        );
      } else {
        setDiscounts((prev) =>
          prev.map((d) => (d.id === discount.id ? { ...d, is_active: !nextActive } : d))
        );
        toast.error(response?.message || 'Failed to update discount status');
      }
    } catch (error) {
      setDiscounts((prev) =>
        prev.map((d) => (d.id === discount.id ? { ...d, is_active: !nextActive } : d))
      );
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404 ? 'Discount not found' : null) ||
        error?.message ||
        'Failed to update discount status';
      toast.error(message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await deleteApi(`/admin/discounts/${deleteTarget.id}`);

      if (response?.statusCode === 200) {
        toast.success(response?.message || 'Discount deleted successfully');
        setDeleteTarget(null);
        setListRefreshKey((key) => key + 1);
      } else {
        toast.error(response?.message || 'Failed to delete discount');
      }
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404 ? 'Discount not found' : null) ||
        error?.message ||
        'Failed to delete discount';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const renderStatus = (discount) => {
    if (isExpired(discount)) {
      return <AdminBadge variant="danger">Expired</AdminBadge>;
    }
    return discount.is_active ? (
      <AdminBadge variant="success">Active</AdminBadge>
    ) : (
      <AdminBadge variant="muted">Inactive</AdminBadge>
    );
  };

  return (
    <div className="admin-page admin-page--list">
      <div className="dashboard-stats">
        <AdminStatCard
          label="Total Discounts"
          value={stats.total.toLocaleString()}
          icon="/assets/images/DiscountIcon.svg"
        />
        <AdminStatCard
          label="Active"
          value={stats.active.toLocaleString()}
          icon="/assets/images/sidebardashboardIcon.svg"
        />
        <AdminStatCard
          label="Expired"
          value={stats.expired.toLocaleString()}
          icon="/assets/images/SearchIcon.svg"
        />
        <AdminStatCard
          label="Redemptions"
          value={stats.redemptions.toLocaleString()}
          icon="/assets/images/UsersTenantDetail.svg"
        />
      </div>

      <div className="admin-toolbar">
        <AdminSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by discount code..."
          fullWidth
        />
        <Button variant="success" onClick={openAddModal}>
          Add Discount
        </Button>
      </div>

      <div className="admin-table-wrap">
        <div className="admin-table-scroll">
        <table className="admin-table admin-table--compact">
          <thead>
            <tr>
              <th className="admin-table-sn">S.No.</th>
              <th>Code</th>
              <th>Discount</th>
              <th>Applies To</th>
              <th>Redemptions</th>
              <th>Expires</th>
              <th>Status</th>
              <th className="admin-table-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="admin-table-empty">
                  Loading discounts...
                </td>
              </tr>
            ) : discounts.length === 0 ? (
              <tr>
                <td colSpan={8} className="admin-table-empty">
                  {debouncedSearch.trim()
                    ? 'No discounts match your search.'
                    : 'No discount codes yet.'}
                </td>
              </tr>
            ) : (
              discounts.map((discount, index) => (
                <tr key={discount.id}>
                  <td className="admin-table-sn">
                    {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
                  </td>
                  <td style={{ fontWeight: 600, letterSpacing: '0.04em' }}>{discount.code}</td>
                  <td>{discount.discount_value}%</td>
                  <td>{appliesToLabel(discount.applies_to)}</td>
                  <td>
                    {discount.times_redeemed}
                    {discount.max_redemptions > 0 ? ` / ${discount.max_redemptions}` : ' / ∞'}
                  </td>
                  <td style={{ fontSize: '13px' }}>{formatExpiry(discount.expires_at)}</td>
                  <td>{renderStatus(discount)}</td>
                  <td className="admin-table-actions">
                    <div className="admin-row-actions">
                      <label
                        className="admin-switch"
                        title={discount.is_active ? 'Deactivate discount' : 'Activate discount'}
                      >
                        <input
                          type="checkbox"
                          aria-label={
                            discount.is_active
                              ? `Deactivate ${discount.code}`
                              : `Activate ${discount.code}`
                          }
                          checked={discount.is_active}
                          disabled={togglingId === discount.id}
                          onChange={() => handleToggleActive(discount)}
                        />
                        <span className="admin-switch-slider" aria-hidden="true" />
                      </label>
                      <Button
                        size="small"
                        variant="outline"
                        onClick={() => openEditModal(discount)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="danger"
                        onClick={() => setDeleteTarget(discount)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {!isLoading && pagination.totalItems > 0 && (
          <AdminPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <AdminModal
        open={addOpen}
        onClose={closeAddModal}
        title={editingId ? 'Edit Discount Code' : 'Create Discount Code'}
        footer={
          <AdminModalFooter
            onCancel={closeAddModal}
            onConfirm={handleSave}
            confirmLabel={
              isSaving
                ? editingId
                  ? 'Saving...'
                  : 'Creating...'
                : editingId
                  ? 'Save Changes'
                  : 'Create Discount'
            }
            confirmDisabled={isSaving || !form.code.trim() || !form.discount_value}
            cancelDisabled={isSaving}
          />
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            label="Discount code"
            placeholder="e.g. SAVE10"
            value={form.code}
            onChange={(e) => updateForm('code', e.target.value.toUpperCase())}
            disabled={isSaving}
          />
          <Input
            label="Discount percentage"
            type="number"
            minValue={1}
            placeholder="10"
            value={form.discount_value}
            onChange={(e) => updateForm('discount_value', e.target.value)}
            disabled={isSaving}
          />
          <div>
            <label
              style={{ display: 'block', marginBottom: 8, fontSize: '14px', fontWeight: 500 }}
            >
              Applies to
            </label>
            <select
              className="admin-select"
              style={{ width: '100%', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
              value={form.applies_to}
              onChange={(e) => updateForm('applies_to', e.target.value)}
              disabled
            >
              {APPLIES_TO_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Max redemptions"
            type="number"
            minValue={1}
            placeholder="Leave empty for unlimited"
            value={form.max_redemptions}
            onChange={(e) => updateForm('max_redemptions', e.target.value)}
            disabled={isSaving}
          />
          <Input
            label="Expires on"
            type="date"
            min={editingId ? undefined : todayISODate()}
            value={form.expires_at}
            onChange={(e) => updateForm('expires_at', e.target.value)}
            disabled={isSaving}
          />
          <p style={{ margin: 0, fontSize: 13, color: '#7c7e93' }}>
            Leave max redemptions and expiry empty for an unlimited, never-expiring code.
          </p>
        </div>
      </AdminModal>

      <AdminModal
        open={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        title="Delete Discount"
        description={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.code}"? The Stripe coupon and promotion code will be removed and the discount can no longer be applied. This action cannot be undone.`
            : ''
        }
        footer={
          <AdminModalFooter
            onCancel={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
            confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
            confirmDisabled={isDeleting}
            cancelDisabled={isDeleting}
            danger
          />
        }
      />
    </div>
  );
}
